import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import t from 'format-message';
import { Checkbox } from '@instructure/ui-checkbox';
import { Flex } from '@instructure/ui-flex';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import { IconArrowOpenDownLine, IconArrowOpenEndLine, IconTrashLine } from '@instructure/ui-icons';
import { IconButton } from '@instructure/ui-buttons';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Popover } from '@instructure/ui-popover';
import { TruncateText } from '@instructure/ui-truncate-text';
import { View } from '@instructure/ui-view';
import OutcomeDescription from "../OutcomeDescription/index.js";
import { outcomeShape } from "../../store/shapes.js";
import useBoolean from "../../hooks/useBoolean.js";
var AlignmentItem = function AlignmentItem(_ref) {
  var outcome = _ref.outcome,
    removeAlignment = _ref.removeAlignment,
    canManageOutcomes = _ref.canManageOutcomes,
    isTray = _ref.isTray,
    shouldFocus = _ref.shouldFocus,
    isOutcomeSelected = _ref.isOutcomeSelected,
    selectOutcomeIds = _ref.selectOutcomeIds,
    deselectOutcomeIds = _ref.deselectOutcomeIds,
    features = _ref.features;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    truncated = _useState2[0],
    setTruncated = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    titleTruncated = _useState4[0],
    setTitleTruncated = _useState4[1];
  var id = outcome.id,
    label = outcome.label,
    title = outcome.title,
    description = outcome.description,
    friendly_description = outcome.friendly_description,
    scoring_method = outcome.scoring_method;
  var algorithm = scoring_method.algorithm,
    algorithm_data = scoring_method.algorithm_data,
    mastery_percent = scoring_method.mastery_percent,
    points_possible = scoring_method.points_possible,
    scoring_tiers = scoring_method.scoring_tiers;
  var trashIconRef = useRef();
  var toggleExpandOutcomeDetails = function toggleExpandOutcomeDetails() {
    return setTruncated(function (prevState) {
      return !prevState;
    });
  };
  var _useBoolean = useBoolean(false),
    _useBoolean2 = _slicedToArray(_useBoolean, 3),
    isShowingTooltip = _useBoolean2[0],
    showTooltip = _useBoolean2[1],
    hideTooltip = _useBoolean2[2];
  var displayTitle = !canManageOutcomes && label ? label : title;
  useEffect(function () {
    if (shouldFocus) {
      var _trashIconRef$current;
      (_trashIconRef$current = trashIconRef.current) === null || _trashIconRef$current === void 0 ? void 0 : _trashIconRef$current.focus();
    }
  }, [shouldFocus]);
  var toggleOutcomeSelection = function toggleOutcomeSelection() {
    if (isOutcomeSelected(id)) {
      deselectOutcomeIds([id]);
    } else {
      selectOutcomeIds([id]);
    }
  };
  var handleUpdate = function handleUpdate(isTruncated) {
    if (titleTruncated !== isTruncated) {
      setTitleTruncated(isTruncated);
    }
  };
  var renderOutcomeTitle = function renderOutcomeTitle() {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        padding: isTray ? '0.5rem 0' : '0.4rem 0 0 0.3rem'
      },
      "data-automation": "alignmentItem__outcomeName"
    }, truncated ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading, {
      level: "h4",
      as: "h2",
      theme: {
        lineHeight: '1.5rem'
      }
    }, /*#__PURE__*/React.createElement(TruncateText, {
      position: "middle",
      onUpdate: handleUpdate
    }, displayTitle, /*#__PURE__*/React.createElement("span", null)))) : /*#__PURE__*/React.createElement(Heading, {
      level: "h4",
      as: "h2",
      theme: {
        lineHeight: '1.5rem'
      }
    }, displayTitle));
  };
  var renderDeleteButton = function renderDeleteButton() {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        padding: '0.2rem 0 0'
      },
      "data-automation": "outcomeAlignmentItem__delete"
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      withBackground: false,
      withBorder: false,
      screenReaderLabel: t('Remove {title}', {
        title: title
      }),
      onClick: removeAlignment,
      ref: trashIconRef
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: '1rem'
      }
    }, /*#__PURE__*/React.createElement(IconTrashLine, null))));
  };
  return /*#__PURE__*/React.createElement(View, {
    as: "div",
    padding: "x-small 0",
    "data-automation": "outcomeAlignment__item",
    "data-testid": "alignment-item"
  }, /*#__PURE__*/React.createElement(Flex, {
    as: "div",
    alignItems: "start"
  }, /*#__PURE__*/React.createElement(Flex.Item, {
    as: "div",
    size: isTray ? '4.5rem' : '2rem'
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '0.3125rem 0'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, isTray && /*#__PURE__*/React.createElement(Flex.Item, {
    as: "div",
    padding: "0 0 0 xx-small"
  }, /*#__PURE__*/React.createElement("div", {
    className: "OutcomeSelector"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    label: /*#__PURE__*/React.createElement(ScreenReaderContent, null, t('Select outcome {title}', {
      title: title
    })),
    value: "medium",
    checked: isOutcomeSelected(id),
    onChange: toggleOutcomeSelection
  }))), /*#__PURE__*/React.createElement(Flex.Item, {
    as: "div",
    "data-automation": "alignmentItem-expand-description"
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    screenReaderLabel: truncated ? t('Expand description for outcome {title}', {
      title: title
    }) : t('Collapse description for outcome {title}', {
      title: title
    }),
    withBackground: false,
    withBorder: false,
    onClick: toggleExpandOutcomeDetails
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignSelf: 'center',
      fontSize: '0.875rem'
    }
  }, truncated ? /*#__PURE__*/React.createElement(IconArrowOpenEndLine, null) : /*#__PURE__*/React.createElement(IconArrowOpenDownLine, null))))))), /*#__PURE__*/React.createElement(Flex.Item, {
    alignItems: "center",
    size: "50%",
    shouldGrow: true
  }, truncated && titleTruncated ? /*#__PURE__*/React.createElement(Popover, {
    renderTrigger: renderOutcomeTitle(),
    isShowingContent: isShowingTooltip,
    onShowContent: showTooltip,
    onHideContent: hideTooltip,
    color: "primary-inverse",
    placement: "top start",
    offsetY: '-10rem'
  }, /*#__PURE__*/React.createElement(View, {
    as: "div",
    maxWidth: "19rem",
    padding: "small"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "normal",
    wrap: "break-word"
  }, displayTitle))) : renderOutcomeTitle()), canManageOutcomes && !isTray && /*#__PURE__*/React.createElement(Flex.Item, null, renderDeleteButton())), /*#__PURE__*/React.createElement(Flex, {
    as: "div",
    alignItems: "start"
  }, /*#__PURE__*/React.createElement(Flex.Item, {
    size: isTray ? '4.5rem' : '2.3rem'
  }), /*#__PURE__*/React.createElement(Flex.Item, {
    size: "80%"
  }, /*#__PURE__*/React.createElement(View, {
    as: "div",
    padding: "0 0 x-small"
  }, /*#__PURE__*/React.createElement(OutcomeDescription, {
    label: label,
    description: description,
    friendlyDescription: friendly_description,
    calculationMethod: algorithm,
    calculationInt: algorithm_data,
    masteryPercent: mastery_percent,
    pointsPossible: points_possible,
    ratings: scoring_tiers,
    truncated: truncated,
    isTray: isTray,
    canManageOutcomes: canManageOutcomes,
    features: features
  })))));
};
AlignmentItem.propTypes = {
  outcome: outcomeShape.isRequired,
  removeAlignment: PropTypes.func,
  canManageOutcomes: PropTypes.bool,
  isTray: PropTypes.bool,
  shouldFocus: PropTypes.bool,
  isOutcomeSelected: PropTypes.func,
  selectOutcomeIds: PropTypes.func,
  deselectOutcomeIds: PropTypes.func,
  features: PropTypes.array
};
AlignmentItem.defaultProps = {
  removeAlignment: function removeAlignment() {},
  canManageOutcomes: true,
  isTray: false,
  shouldFocus: false,
  isOutcomeSelected: function isOutcomeSelected() {},
  selectOutcomeIds: function selectOutcomeIds() {},
  deselectOutcomeIds: function deselectOutcomeIds() {},
  features: []
};
export default AlignmentItem;